import {
  useAnimals,
  useLots,
  useRegisterMilkWeighing,
  type DairyEntry,
} from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/lib/api';
import { MILKING_SHIFT_OPTIONS } from '@/lib/dairy';
import { todayIso } from '@/lib/format';
import { useEffect, useState } from 'react';

type FormState = {
  event_date: string;
  cow_id: string;
  milking_shift: string;
  production_liters: string;
  milking_time: string;
  lot_id: string;
  responsible_name: string;
  notes: string;
  fat_pct: string;
  protein_pct: string;
  lactose_pct: string;
  total_solids_pct: string;
  ccs: string;
  cbt: string;
  is_drying_off: boolean;
  drying_off_date: string;
};

function emptyForm(presetCowId?: number): FormState {
  return {
    event_date: todayIso(),
    cow_id: presetCowId ? String(presetCowId) : '',
    milking_shift: 'morning',
    production_liters: '',
    milking_time: '',
    lot_id: '',
    responsible_name: '',
    notes: '',
    fat_pct: '',
    protein_pct: '',
    lactose_pct: '',
    total_solids_pct: '',
    ccs: '',
    cbt: '',
    is_drying_off: false,
    drying_off_date: '',
  };
}

function fromEntry(entry: DairyEntry, targetDate?: string): FormState {
  return {
    event_date: targetDate ?? todayIso(),
    cow_id: String(entry.cow_id),
    milking_shift: entry.milking_shift || 'morning',
    production_liters: String(entry.production_liters ?? ''),
    milking_time: entry.milking_time ? String(entry.milking_time).slice(0, 5) : '',
    lot_id: entry.lot_id ? String(entry.lot_id) : '',
    responsible_name: entry.responsible_name ?? '',
    notes: entry.notes ?? '',
    fat_pct: entry.fat_pct != null ? String(entry.fat_pct) : '',
    protein_pct: entry.protein_pct != null ? String(entry.protein_pct) : '',
    lactose_pct: entry.lactose_pct != null ? String(entry.lactose_pct) : '',
    total_solids_pct: entry.total_solids_pct != null ? String(entry.total_solids_pct) : '',
    ccs: entry.ccs != null ? String(entry.ccs) : '',
    cbt: entry.cbt != null ? String(entry.cbt) : '',
    is_drying_off: entry.is_drying_off,
    drying_off_date: entry.drying_off_date ?? '',
  };
}

export function MilkWeighingFormModal({
  open,
  onClose,
  propertyId,
  presetCowId,
  initialEntry,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  presetCowId?: number;
  initialEntry?: DairyEntry | null;
}) {
  const { data: animals } = useAnimals({ property_id: propertyId, sex: 'female', per_page: 500 });
  const { data: lots } = useLots(propertyId);
  const register = useRegisterMilkWeighing();
  const [form, setForm] = useState(() => emptyForm(presetCowId));
  const [showQuality, setShowQuality] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initialEntry) {
      setForm(fromEntry(initialEntry));
      setShowQuality(
        initialEntry.fat_pct != null ||
          initialEntry.protein_pct != null ||
          initialEntry.ccs != null,
      );
    } else {
      setForm(emptyForm(presetCowId));
      setShowQuality(false);
    }
    setError('');
  }, [open, presetCowId, initialEntry]);

  const cowOptions = (animals?.animals ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));

  const lotOptions = (lots?.lots ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({ value: l.id, label: l.name }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.cow_id) {
      setError('Selecione o animal.');
      return;
    }
    if (!form.production_liters.trim()) {
      setError('Informe a produção em litros.');
      return;
    }

    const body: Record<string, unknown> = {
      property_id: propertyId,
      event_date: form.event_date,
      cow_id: Number(form.cow_id),
      milking_shift: form.milking_shift,
      production_liters: Number(form.production_liters),
      milking_time: form.milking_time || null,
      lot_id: form.lot_id ? Number(form.lot_id) : null,
      responsible_name: form.responsible_name || null,
      notes: form.notes || null,
      is_drying_off: form.is_drying_off,
      drying_off_date: form.drying_off_date || null,
    };

    if (form.fat_pct) body.fat_pct = Number(form.fat_pct);
    if (form.protein_pct) body.protein_pct = Number(form.protein_pct);
    if (form.lactose_pct) body.lactose_pct = Number(form.lactose_pct);
    if (form.total_solids_pct) body.total_solids_pct = Number(form.total_solids_pct);
    if (form.ccs) body.ccs = Number(form.ccs);
    if (form.cbt) body.cbt = Number(form.cbt);

    try {
      await register.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao registrar pesagem de leite.'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pesagem de leite"
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="milk-weighing-form" loading={register.isPending}>
            Registrar
          </Button>
        </>
      }
    >
      <form id="milk-weighing-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Animal"
            required
            value={form.cow_id}
            onChange={(e) => setForm({ ...form, cow_id: e.target.value })}
            placeholder="Selecione…"
            options={cowOptions}
          />
          <DateInput
            label="Data"
            required
            value={form.event_date}
            onChange={(event_date) => setForm({ ...form, event_date })}
          />
          <Select
            label="Turno"
            required
            value={form.milking_shift}
            onChange={(e) => setForm({ ...form, milking_shift: e.target.value })}
            options={MILKING_SHIFT_OPTIONS}
          />
          <Input
            label="Produção (litros)"
            required
            type="number"
            step="0.1"
            min="0"
            value={form.production_liters}
            onChange={(e) => setForm({ ...form, production_liters: e.target.value })}
          />
          <Input
            label="Hora"
            type="time"
            value={form.milking_time}
            onChange={(e) => setForm({ ...form, milking_time: e.target.value })}
          />
          <Select
            label="Lote"
            value={form.lot_id}
            onChange={(e) => setForm({ ...form, lot_id: e.target.value })}
            placeholder="Opcional"
            options={lotOptions}
          />
          <Input
            label="Ordenhador"
            value={form.responsible_name}
            onChange={(e) => setForm({ ...form, responsible_name: e.target.value })}
          />
        </div>

        <Input
          label="Observações"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <label className="flex items-center gap-2 text-sm text-manejai-bark">
          <input
            type="checkbox"
            checked={form.is_drying_off}
            onChange={(e) => setForm({ ...form, is_drying_off: e.target.checked })}
          />
          Secagem
        </label>
        {form.is_drying_off && (
          <DateInput
            label="Data da secagem"
            value={form.drying_off_date}
            onChange={(drying_off_date) => setForm({ ...form, drying_off_date })}
          />
        )}

        <button
          type="button"
          className="text-sm font-medium text-manejai-moss hover:underline"
          onClick={() => setShowQuality((v) => !v)}
        >
          {showQuality ? 'Ocultar qualidade do leite' : 'Qualidade do leite (opcional)'}
        </button>

        {showQuality && (
          <div className="grid gap-3 sm:grid-cols-3 rounded-xl border border-manejai-sand/70 p-4">
            <Input
              label="Gordura (%)"
              type="number"
              step="0.01"
              value={form.fat_pct}
              onChange={(e) => setForm({ ...form, fat_pct: e.target.value })}
            />
            <Input
              label="Proteína (%)"
              type="number"
              step="0.01"
              value={form.protein_pct}
              onChange={(e) => setForm({ ...form, protein_pct: e.target.value })}
            />
            <Input
              label="Lactose (%)"
              type="number"
              step="0.01"
              value={form.lactose_pct}
              onChange={(e) => setForm({ ...form, lactose_pct: e.target.value })}
            />
            <Input
              label="Sólidos totais (%)"
              type="number"
              step="0.01"
              value={form.total_solids_pct}
              onChange={(e) => setForm({ ...form, total_solids_pct: e.target.value })}
            />
            <Input
              label="CCS"
              type="number"
              value={form.ccs}
              onChange={(e) => setForm({ ...form, ccs: e.target.value })}
            />
            <Input
              label="CBT"
              type="number"
              value={form.cbt}
              onChange={(e) => setForm({ ...form, cbt: e.target.value })}
            />
          </div>
        )}
      </form>
    </Modal>
  );
}
